import { Button } from "@/components/ui/button";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { useIsInWatchlistQuery, useToggleWatchlist } from "@/hooks/useWatchlist";
import { useAuth } from "@/hooks/useAuth";
import { usePermissions } from "@/hooks/usePermissions";
import { OpuPermissions } from "@/lib/permissions";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

interface WatchlistButtonProps {
  auctionKey: string;
  variant?: "icon" | "default";
  className?: string;
}

const WatchlistButton = ({ auctionKey, variant = "icon", className }: WatchlistButtonProps) => {
  const { user } = useAuth();
  const { has, isLoading: isPermLoading } = usePermissions();
  const { data: isInWatchlist, isLoading: isChecking } = useIsInWatchlistQuery(user?.id, auctionKey);
  const { toggle, isPending } = useToggleWatchlist();

  // Don't render if user is not logged in or lacks USE_WATCHLIST permission
  if (!user) return null;
  if (!isPermLoading && !has(OpuPermissions.USE_WATCHLIST)) return null;

  const handleClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await toggle(auctionKey, isInWatchlist || false);
    } catch (error) {
      console.error("Watchlist toggle error:", error);
      toast.error("Fehler bei der Watchlist-Aktion");
    }
  };

  const isLoading = isChecking || isPending;

  if (variant === "icon") {
    return (
      <Button
        variant="ghost"
        size="icon"
        className={cn(
          "h-8 w-8 rounded-full",
          isInWatchlist && "text-primary bg-primary/10",
          className
        )}
        onClick={handleClick}
        disabled={isLoading}
      >
        {isLoading ? (
          <Loader2 size={16} className="animate-spin" />
        ) : isInWatchlist ? (
          <EyeOff size={16} />
        ) : (
          <Eye size={16} />
        )}
      </Button>
    );
  }

  return (
    <Button
      variant={isInWatchlist ? "secondary" : "outline"}
      size="sm"
      className={cn("gap-2", className)}
      onClick={handleClick}
      disabled={isLoading}
    >
      {isLoading ? (
        <Loader2 size={14} className="animate-spin" />
      ) : isInWatchlist ? (
        <>
          <EyeOff size={14} />
          Entfernen
        </>
      ) : (
        <>
          <Eye size={14} />
          Beobachten
        </>
      )}
    </Button>
  );
};

export default WatchlistButton;
